import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth0 } from './auth/useAuth';

import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import PipelinePage from './pages/PipelinePage';
import CampaignsPage from './pages/CampaignsPage';
import CampaignBuilderPage from './pages/CampaignBuilderPage';
import LeadDetailPage from './pages/LeadDetailPage';
import NewLeadPage from './pages/NewLeadPage';
import HostedPagesPage from './pages/HostedPagesPage';
import HostedPageDetailPage from './pages/HostedPageDetailPage';
import AviaryV2Page from './pages/AviaryV2Page';
import AviaryV2CampsPage from './pages/AviaryV2CampsPage';
import AviaryV2CampaignDetailPage from './pages/AviaryV2CampaignDetailPage';
import AviaryV2LeadDetailPage from './pages/AviaryV2LeadDetailPage';
import AviaryV2BoardCoachPage from './pages/AviaryV2BoardCoachPage';
import AviaryV2BoardBusinessPage from './pages/AviaryV2BoardBusinessPage';
import AviaryV2BoardRedirectPage from './pages/AviaryV2BoardRedirectPage';
import AviaryV2CampaignReviewPage from './pages/AviaryV2CampaignReviewPage';
import AviaryV2ArticlesPage from './pages/AviaryV2ArticlesPage';
import AviaryV2ArticleNewPage from './pages/AviaryV2ArticleNewPage';
import AviaryV2ArticleNewUploadPage from './pages/AviaryV2ArticleNewUploadPage';
import AviaryV2ArticleNewLinkPage from './pages/AviaryV2ArticleNewLinkPage';
import AviaryV2ArticleNewExternalPage from './pages/AviaryV2ArticleNewExternalPage';
import AviaryV2ArticleNewCocreatePage from './pages/AviaryV2ArticleNewCocreatePage';
import AviaryV2ArticleCowritePage from './pages/AviaryV2ArticleCowritePage';
import AviaryV2ArticleDetailPage from './pages/AviaryV2ArticleDetailPage';
import AviaryV2ArticlePromotePage from './pages/AviaryV2ArticlePromotePage';
import AviaryV2KnowledgeBasePage from './pages/AviaryV2KnowledgeBasePage';
import AviaryV2OptOutsPage from './pages/AviaryV2OptOutsPage';
import AviaryV2SettingsPage from './pages/AviaryV2SettingsPage';
import AviaryV2ShareRedirectPage from './pages/AviaryV2ShareRedirectPage';
import LoadingSpinner from './components/LoadingSpinner';
import AviaryShell from './layout/AviaryShell';
import AviaryV2Shell from './layout/AviaryV2Shell';

function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth0();
  if (isLoading) return <LoadingSpinner fullScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  const { isLoading } = useAuth0();

  if (isLoading) return <LoadingSpinner fullScreen />;

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        {/* v1.40: v2 Today is the default landing page. v1 routes below stay
            reachable by direct URL (see the Legacy card on Settings). */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2Page />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2Page />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/pipeline"
          element={
            <ProtectedRoute>
              <AviaryShell>
                <PipelinePage />
              </AviaryShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/campaigns"
          element={
            <ProtectedRoute>
              <AviaryShell>
                <CampaignsPage />
              </AviaryShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/campaigns/:id"
          element={
            <ProtectedRoute>
              <AviaryShell>
                <CampaignBuilderPage />
              </AviaryShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/leads/new"
          element={
            <ProtectedRoute>
              <AviaryShell>
                <NewLeadPage />
              </AviaryShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/leads/:id"
          element={
            <ProtectedRoute>
              <AviaryShell>
                <LeadDetailPage />
              </AviaryShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/hosting"
          element={
            <ProtectedRoute>
              <AviaryShell>
                <HostedPagesPage />
              </AviaryShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/hosting/:slug"
          element={
            <ProtectedRoute>
              <AviaryShell>
                <HostedPageDetailPage />
              </AviaryShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/v2"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2Page />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/v2/campaigns"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2CampsPage />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/v2/campaigns/:id"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2CampaignDetailPage />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/v2/campaigns/:id/review"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2CampaignReviewPage />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/v2/leads/:id"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2LeadDetailPage />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/v2/board"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2BoardRedirectPage />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/v2/board/people"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2BoardCoachPage />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/v2/board/businesses"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2BoardBusinessPage />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/v2/pipeline/share"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2ShareRedirectPage />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/v2/articles"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2ArticlesPage />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/v2/articles/new"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2ArticleNewPage />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/v2/articles/new/upload"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2ArticleNewUploadPage />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/v2/articles/new/external"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2ArticleNewExternalPage />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/v2/articles/new/link"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2ArticleNewLinkPage />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/v2/articles/new/cocreate"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2ArticleNewCocreatePage />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/v2/articles/:slug/cowrite"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2ArticleCowritePage />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/v2/articles/:slug"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2ArticleDetailPage />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/v2/articles/:slug/promote"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2ArticlePromotePage />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/v2/knowledge-base"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2KnowledgeBasePage />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/v2/opt-outs"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2OptOutsPage />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/v2/settings"
          element={
            <ProtectedRoute>
              <AviaryV2Shell>
                <AviaryV2SettingsPage />
              </AviaryV2Shell>
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/v2" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
